import React from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { VerticalStack } from './Stack';
import { DescriptionText } from './Typography';

export interface SectionHeadingProps {
  title: string;
  subtitle?: string;
  className?: string;
}

export function SectionHeading({ title, subtitle, className }: SectionHeadingProps) {
  return (
    <VerticalStack gap={1} className={className}>
      <h2 className="text-lg font-black tracking-tight text-foreground">{title}</h2>
      {subtitle && <DescriptionText className="hidden text-xs sm:block">{subtitle}</DescriptionText>}
    </VerticalStack>
  );
}

export interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  children: ReactNode;
  className?: string;
  description?: string;
}

export function Field({ label, htmlFor, error, children, className, description }: FieldProps) {
  return (
    <VerticalStack gap={2} className={className}>
      <label htmlFor={htmlFor} className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
        {label}
      </label>
      {children}
      {error && <p className="text-[10px] font-bold text-destructive">{error}</p>}
      {description && <DescriptionText className="text-[11px]">{description}</DescriptionText>}
    </VerticalStack>
  );
}

export const inputStyles = "w-full rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm font-bold text-foreground outline-none transition-all focus:border-primary/40 focus:bg-background disabled:opacity-50";
export const labelStyles = "text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1.5 block";

export interface CurrencyInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  currencySymbol?: string;
}

export const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, currencySymbol = '$', inputMode = 'decimal', ...props }, ref) => (
      <div className="relative w-full flex items-center">
        <span className="absolute left-3 text-muted-foreground font-bold text-sm select-none pointer-events-none">
          {currencySymbol}
        </span>
        <input
          type="text"
          inputMode={inputMode}
          className={cn(inputStyles, "pl-7", className)}
          ref={ref}
          {...props}
        />
      </div>
    )
);
CurrencyInput.displayName = 'CurrencyInput';

export interface PercentageInputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

export const PercentageInput = React.forwardRef<HTMLInputElement, PercentageInputProps>(
  ({ className, ...props }, ref) => {
    return (
      <div className="relative w-full flex items-center">
        <input
          type="text"
          className={cn(inputStyles, "pr-7", className)}
          ref={ref}
          {...props}
        />
        <span className="absolute right-3 text-muted-foreground font-bold text-sm select-none pointer-events-none">
          %
        </span>
      </div>
    );
  }
);
PercentageInput.displayName = 'PercentageInput';
